import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { api, ApiError } from '../../api';
import { t } from '../../i18n';
import { showToast } from '../../components/ui/toast';

type Step = 'contract' | 'password' | 'profile' | 'done';

interface OnboardingState {
  step: Step;
  student: { first_name: string; last_name: string; email: string };
  contract: { version: number; content_html: string } | null;
}

/** Public multi-step onboarding: contract → password → profile. The token is the
 *  only credential; on completion the server issues a portal session. */
@customElement('cadence-onboarding-page')
export class CadenceOnboardingPage extends LitElement {
  @property() token = '';
  @state() private loading = true;
  @state() private invalid = false;
  @state() private step: Step = 'contract';
  @state() private data: OnboardingState | null = null;

  @state() private agreed = false;
  @state() private password = '';
  @state() private confirm = '';
  @state() private pwError = '';
  @state() private profile: Record<string, string> = {};
  @state() private submitting = false;

  static styles = css`
    :host { display: grid; place-items: center; min-height: 100vh; padding: var(--space-4); }
    .card {
      width: 100%; max-width: 640px; background: var(--color-bg-primary);
      border: 1px solid var(--color-border); border-radius: var(--radius-lg);
      box-shadow: var(--shadow-md); padding: var(--space-6);
    }
    .brand { display: flex; align-items: center; gap: 10px; margin-bottom: var(--space-4); }
    .brand .dot { width: 14px; height: 14px; border-radius: 50%; background: var(--color-primary); }
    .brand h1 { margin: 0; font-size: 1.3rem; }
    .steps { display: flex; gap: 8px; margin-bottom: var(--space-5); }
    .steps .s { flex: 1; height: 6px; border-radius: 999px; background: var(--color-border); }
    .steps .s.active, .steps .s.done { background: var(--color-primary); }
    h2 { margin: 0 0 4px; font-size: 1.15rem; }
    p.sub { margin: 0 0 var(--space-4); color: var(--color-text-secondary); font-size: 0.92rem; }
    .contract {
      max-height: 320px; overflow-y: auto; border: 1px solid var(--color-border);
      border-radius: var(--radius-md); padding: var(--space-4); margin-bottom: var(--space-4);
      background: var(--color-bg-secondary); color: var(--color-text-primary);
    }
    .contract :first-child { margin-top: 0; }
    form { display: flex; flex-direction: column; gap: var(--space-4); }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-4); }
    .actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: var(--space-2); }
    .photo { display: flex; justify-content: center; margin-bottom: var(--space-2); }
    .center { text-align: center; padding: var(--space-5) 0; }
    .hint { color: var(--color-text-secondary); font-size: 0.82rem; }
    @media (max-width: 560px) { .grid { grid-template-columns: 1fr; } }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }
  updated(changed: Map<string, unknown>) { if (changed.has('token')) this.load(); }

  private async load() {
    if (!this.token) return;
    this.loading = true; this.invalid = false;
    try {
      const state = await api.get<OnboardingState>(`/onboarding/${this.token}`);
      this.data = state;
      this.step = state.step;
    } catch (e) {
      this.invalid = true;
    } finally {
      this.loading = false;
    }
  }

  private async acceptContract() {
    if (!this.agreed) return;
    this.submitting = true;
    try {
      await api.post(`/onboarding/${this.token}/accept`);
      this.step = 'password';
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.submitting = false; }
  }

  private async submitPassword() {
    this.pwError = '';
    if (this.password.length < 8) { this.pwError = t('common.onboarding.weak'); return; }
    if (this.password !== this.confirm) { this.pwError = t('common.onboarding.mismatch'); return; }
    this.submitting = true;
    try {
      await api.post(`/onboarding/${this.token}/password`, { password: this.password });
      this.step = 'profile';
    } catch (e) {
      this.pwError = e instanceof ApiError ? e.message : (e as Error).message;
    } finally { this.submitting = false; }
  }

  private async submitProfile() {
    this.submitting = true;
    try {
      await api.put(`/onboarding/${this.token}/profile`, this.profile);
      this.step = 'done';
      // The server issued a session cookie; a full reload restores it via boot().
      setTimeout(() => { window.location.href = '/portal/dashboard'; }, 1200);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.submitting = false; }
  }

  private setP(key: string, value: string) { this.profile = { ...this.profile, [key]: value }; }

  private renderSteps() {
    const order: Step[] = ['contract', 'password', 'profile'];
    const idx = order.indexOf(this.step === 'done' ? 'profile' : this.step);
    return html`<div class="steps">
      ${order.map((_, i) => html`<span class="s ${i < idx ? 'done' : ''} ${i === idx ? 'active' : ''}"></span>`)}
    </div>`;
  }

  render() {
    if (this.loading) return html`<div class="card"><cadence-loading></cadence-loading></div>`;
    if (this.invalid) {
      return html`<div class="card"><div class="brand"><span class="dot"></span><h1>${t('common.app.name')}</h1></div>
        <h2>${t('common.onboarding.invalid')}</h2>
        <p class="sub"><a href="/portal" data-link>${t('auth.portalLogin.title')}</a></p></div>`;
    }
    const s = this.data!;
    return html`
      <div class="card">
        <div class="brand"><span class="dot"></span><h1>${t('common.app.name')}</h1></div>
        ${this.renderSteps()}
        ${this.step === 'contract' ? this.renderContract(s) : ''}
        ${this.step === 'password' ? this.renderPassword() : ''}
        ${this.step === 'profile' ? this.renderProfile() : ''}
        ${this.step === 'done' ? html`<div class="center"><h2>${t('common.onboarding.completed')}</h2><cadence-loading></cadence-loading></div>` : ''}
      </div>
    `;
  }

  private renderContract(s: OnboardingState) {
    return html`
      <h2>${t('common.onboarding.stepContract')}</h2>
      <p class="sub">${t('common.onboarding.subtitle')}</p>
      ${s.contract
        ? html`<div class="contract">${unsafeHTML(s.contract.content_html)}</div>
            <cadence-checkbox label=${t('common.onboarding.agree')} ?checked=${this.agreed}
              @change=${(e: CustomEvent) => (this.agreed = e.detail)}></cadence-checkbox>
            <div class="actions">
              <cadence-button ?loading=${this.submitting} @click=${() => this.acceptContract()} ?disabled=${!this.agreed}>
                ${t('common.actions.continue')}
              </cadence-button>
            </div>`
        : html`<p class="sub">${t('common.onboarding.noContract')}</p>`}
    `;
  }

  private renderPassword() {
    return html`
      <h2>${t('common.onboarding.stepPassword')}</h2>
      <p class="sub">${t('common.onboarding.passwordHint')}</p>
      <form @submit=${(e: Event) => { e.preventDefault(); this.submitPassword(); }}>
        ${this.pwError ? html`<p class="hint" style="color:var(--color-danger)" role="alert">${this.pwError}</p>` : ''}
        <cadence-input label=${t('common.onboarding.password')} type="password" autocomplete="new-password"
          .value=${this.password} @input=${(e: CustomEvent) => (this.password = e.detail)}></cadence-input>
        <cadence-input label=${t('common.onboarding.confirmPassword')} type="password" autocomplete="new-password"
          .value=${this.confirm} @input=${(e: CustomEvent) => (this.confirm = e.detail)}
          @enter=${() => this.submitPassword()}></cadence-input>
        <div class="actions">
          <cadence-button type="submit" ?loading=${this.submitting} @click=${() => this.submitPassword()}>
            ${t('common.actions.continue')}
          </cadence-button>
        </div>
      </form>
    `;
  }

  private renderProfile() {
    const s = this.data!;
    const name = `${s.student.first_name ?? ''} ${s.student.last_name ?? ''}`;
    return html`
      <h2>${t('common.onboarding.stepProfile')}</h2>
      <p class="sub">${t('common.onboarding.subtitle')}</p>
      <form @submit=${(e: Event) => { e.preventDefault(); this.submitProfile(); }}>
        <div class="photo">
          <cadence-photo-upload endpoint=${`/onboarding/${this.token}/photo`} name=${name} size="110"></cadence-photo-upload>
        </div>
        <div class="grid">
          <cadence-input label=${t('onboarding.profile.dateOfBirth')} type="date" .value=${this.profile.date_of_birth ?? ''}
            @input=${(e: CustomEvent) => this.setP('date_of_birth', e.detail)}></cadence-input>
          <cadence-input label=${t('onboarding.profile.phone')} type="tel" .value=${this.profile.phone ?? ''}
            @input=${(e: CustomEvent) => this.setP('phone', e.detail)}></cadence-input>
          <cadence-input label=${t('onboarding.profile.emergencyContactName')} .value=${this.profile.emergency_contact_name ?? ''}
            @input=${(e: CustomEvent) => this.setP('emergency_contact_name', e.detail)}></cadence-input>
          <cadence-input label=${t('onboarding.profile.emergencyContactPhone')} type="tel" .value=${this.profile.emergency_contact_phone ?? ''}
            @input=${(e: CustomEvent) => this.setP('emergency_contact_phone', e.detail)}></cadence-input>
          <cadence-input label=${t('onboarding.profile.relationship')} .value=${this.profile.emergency_contact_relation ?? ''}
            @input=${(e: CustomEvent) => this.setP('emergency_contact_relation', e.detail)}></cadence-input>
          <cadence-input label=${t('onboarding.profile.medicalNotes')} .value=${this.profile.medical_notes ?? ''}
            @input=${(e: CustomEvent) => this.setP('medical_notes', e.detail)}></cadence-input>
        </div>
        <div class="actions">
          <cadence-button type="submit" ?loading=${this.submitting} @click=${() => this.submitProfile()}>
            ${t('common.onboarding.finish')}
          </cadence-button>
        </div>
      </form>
    `;
  }
}
